import { Check, X } from "lucide-react";

const features = [
  {
    category: "Core Features",
    items: [
      {
        name: "Campaign Count",
        description: "Maximum number of campaigns a brand can run in a month",
        plan1: "15",
        plan2: "60",
        isNumber: true,
      },
      {
        name: "Collaboration Count",
        description: "Total influencer-brand collaborations allowed each month",
        plan1: "30",
        plan2: "120",
        isNumber: true,
      },
      {
        name: "Campaign Execution System",
        description: "End-to-end tech for campaign execution and tracking",
        plan1: true,
        plan2: true,
      },
    ],
  },
  {
    category: "Analytics and Reporting",
    items: [
      {
        name: "Real-Time Performance Tracking",
        description: "Monitor live campaign performance metrics in real-time",
        plan1: "TRUE",
        plan2: "TRUE",
        isNumber: null,
      },
      {
        name: "Audience Reach Analysis",
        description: "Insights into the demographics of who saw the campaign",
        plan1: "30",
        plan2: "120",
        isNumber: true,
      },
      {
        name: "Audience Engagement Analysis",
        description: "AI-analyzed data on who engaged with the campaign",
        plan1: "30",
        plan2: "120",
        isNumber: true,
      },
      {
        name: "Brand Sentiment Insights",
        description:
          "Analysis of competitor mentions, comment themes, and audience sentiment",
        plan1: "30",
        plan2: "120",
        isNumber: true,
      },
      {
        name: "Campaign Overview Report",
        description:
          "Summary of the campaign’s overall performance across influencers",
        plan1: "15",
        plan2: "60",
        isNumber: true,
      },
      {
        name: "Campaign Report Delivery",
        description: "Campaign reports emailed directly to your inbox",
        plan1: "TRUE",
        plan2: "TRUE",
        isNumber: null,
      },
    ],
  },
  {
    category: "Search and Discovery",
    items: [
      {
        name: "Detailed Influencer Insights",
        description:
          "Comprehensive data on influencers, including past campaigns and audience fit",
        plan1: "Unlimited",
        plan2: "Unlimited",
        isNumber: true,
      },
      {
        name: "AI-Powered Recommendations",
        description:
          "Smart influencer suggestions based on your preferences and filters",
        plan1: "TRUE",
        plan2: "TRUE",
        isNumber: "",
      },
      {
        name: "Advanced Search Filters",
        description:
          "Use detailed filters like audience demographics and engagement rates",
        plan1: "TRUE",
        plan2: "TRUE",
        isNumber: "",
      },
      {
        name: "Manual Filtering",
        description:
          "Customize filters to search for influencers that fit your needs",
        plan1: "TRUE",
        plan2: "TRUE",
        isNumber: "",
      },
      {
        name: "Search Quota",
        description: "Maximum number of searches allowed on the platform",
        plan1: "Unlimited",
        plan2: "Unlimited",
        isNumber: true,
      },
    ],
  },
  {
    category: "Communication and Accessibility",
    items: [
      {
        name: "Team Accounts",
        description: "Number of users who can access your brand account",
        plan1: "2",
        plan2: "3",
        isNumber: true,
      },
      {
        name: "WhatsApp Alerts",
        description: "Instant campaign updates and notifications on WhatsApp",
        plan1: "TRUE",
        plan2: "TRUE",
        isNumber: null,
      },
      {
        name: "Email Notifications",
        description: "Alerts and updates delivered via email",
        plan1: "TRUE",
        plan2: "TRUE",
        isNumber: null,
      },
    ],
  },

  {
    category: "Campaign Optimization",
    items: [
      {
        name: "Data Refresh Requests",
        description:
          "Number of times a brand can request influencers to update their stats",
        plan1: "2",
        plan2: "10",
        isNumber: true,
      },
      {
        name: "Campaign Calendar View",
        description: "Visualize live and scheduled campaigns on a calendar",
        plan1: "TRUE",
        plan2: "TRUE",
        isNumber: null,
      },
      {
        name: "Custom Campaign Scheduling",
        description:
          "Set unique live dates for each influencer in your campaign",
        plan1: "TRUE",
        plan2: "TRUE",
        isNumber: null,
      },
    ],
  },

  {
    category: "Add-Ons",
    items: [
      {
        name: "Additional Campaign",
        description:
          "Add more campaigns to your monthly plan for extended reach and visibility",
        plan1: "1,999",
        plan2: "1,999",
        isPrice: true,
      },
      {
        name: "Extra Collaboration*",
        description:
          "Increase the number of influencer-brand interactions for your campaigns",
        plan1: "799",
        plan2: "799",
        isPrice: true,
      },
      {
        name: "Additional Team Account",
        description:
          "Add more team members to your account with full platform access",
        plan1: "3,999",
        plan2: "3,999",
        isPrice: true,
      },
    ],
  },
];

export default function ComparisonTable() {
  return (
    <section className="bg-gray-50 py-10">
      <div className="container mx-auto px-4 sm:max-w-[90%] md:max-w-[70%]">
        <h2 className="mb-8 text-center text-3xl font-bold">
          Compare Features & Offerings
        </h2>
        <div className="overflow-x-auto">
          <table className="w-full min-w-[640px] border-collapse rounded-lg bg-white shadow-sm">
            <thead>
              <tr className="bg-gray-100">
                <th className="border-b p-4 text-left">Features</th>
                <th className="border-b p-4 text-center">Monthly</th>
                <th className="border-b p-4 text-center">3 Months</th>
              </tr>
            </thead>
            <tbody>
              {features.map((category, categoryIndex) => (
                <>
                  <tr key={`category-${categoryIndex}`} className="bg-gray-50">
                    <td
                      colSpan={3}
                      className="border-b p-4 text-left font-semibold text-gray-700"
                    >
                      {category.category}
                    </td>
                  </tr>
                  {category.items.map((feature, featureIndex) => (
                    <tr
                      key={`feature-${categoryIndex}-${featureIndex}`}
                      className="border-b last:border-b-0"
                    >
                      <td className="p-4 text-left">
                        <p className="font-semibold">{feature.name}</p>
                        <p className="text-sm text-gray-600">
                          {feature.description}
                        </p>
                      </td>
                      <td className="p-4 text-center">
                        {feature.isPrice ? (
                          <span>₹{feature.plan1}</span>
                        ) : feature.isNumber ? (
                          <span>{feature.plan1}</span>
                        ) : feature.isText ? (
                          <span>{feature.plan1}</span>
                        ) : feature.plan1 ? (
                          <Check className="mx-auto h-6 w-6 text-green-500" />
                        ) : (
                          <X className="mx-auto h-6 w-6 text-red-500" />
                        )}
                      </td>
                      <td className="p-4 text-center">
                        {feature.isPrice ? (
                          <span>₹{feature.plan2}</span>
                        ) : feature.isNumber ? (
                          <span>{feature.plan2}</span>
                        ) : feature.isText ? (
                          <span>{feature.plan2}</span>
                        ) : feature.plan2 ? (
                          <Check className="mx-auto h-6 w-6 text-green-500" />
                        ) : (
                          <X className="mx-auto h-6 w-6 text-red-500" />
                        )}
                      </td>
                    </tr>
                  ))}
                </>
              ))}
            </tbody>
          </table>

          <p className="text-gray-600 .text-sm">
            {" "}
            *Includes individual reports, engagement analysis, and integration
            into the overall campaign insights
          </p>
        </div>
      </div>
    </section>
  );
}
